import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Save, Wrench } from "lucide-react";

type Cliente = { id: string; nome: string; documento?: string | null; telefone?: string | null };
type Veiculo = { id: string; placa?: string | null; marca?: string | null; modelo?: string | null; ano?: number | null; km?: any };

export default function OSNew() {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);

  // cliente lookup
  const [qCliente, setQCliente] = useState("");
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clienteId, setClienteId] = useState("");

  // veículos do cliente
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [loadingVeiculos, setLoadingVeiculos] = useState(false);
  const [veiculoId, setVeiculoId] = useState<string>("");

  const [observacaoCliente, setObservacaoCliente] = useState("");
  const [diagnostico, setDiagnostico] = useState("");

  const disabled = useMemo(() => saving || !clienteId, [saving, clienteId]);

  async function loadClientes(query: string) {
    setLoadingClientes(true);
    try {
      const { data } = await api.get("/api/clientes", { params: { q: query, limit: 10 } });
      // supondo { data: [...] } ou { data: { data: [...] } }
      const list = data?.data ?? data?.data?.data ?? [];
      setClientes(list);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao buscar clientes");
    } finally {
      setLoadingClientes(false);
    }
  }

  async function loadVeiculos(cid: string) {
    setLoadingVeiculos(true);
    try {
      const { data } = await api.get("/api/veiculos", { params: { clienteId: cid } });
      const list = data?.data ?? data?.veiculos ?? data ?? [];
      setVeiculos(list);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao buscar veículos");
      setVeiculos([]);
    } finally {
      setLoadingVeiculos(false);
    }
  }

  useEffect(() => {
    if (clienteId) {
      loadVeiculos(clienteId);
      setVeiculoId("");
    } else {
      setVeiculos([]);
      setVeiculoId("");
    }

  }, [clienteId]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (disabled) return;

    const payload: any = {
      clienteId,
      veiculoId: veiculoId ? veiculoId : null,
      observacaoCliente: observacaoCliente.trim() ? observacaoCliente.trim() : null,
      diagnostico: diagnostico.trim() ? diagnostico.trim() : null,
    };

    setSaving(true);
    try {
      const { data } = await api.post("/api/os", payload);
      const osId = data?.os?.id;
      toast.success("OS criada ✅");
      if (osId) navigate(`/os/${osId}`);
      else navigate("/os");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao criar OS");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench size={20} />
              Nova OS
            </h1>
            <p className="text-sm text-slate-500">Selecione um cliente e, se quiser, um veículo.</p>
          </div>

          <Link to="/os">
            <Button variant="outline" className="gap-2">
              <ArrowLeft size={16} />
              Voltar
            </Button>
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <form onSubmit={onSubmit} className="space-y-6">
            {/* Cliente */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Cliente *</label>

              <div className="flex gap-2">
                <Input
                  value={qCliente}
                  onChange={(e) => setQCliente(e.target.value)}
                  placeholder="Digite para buscar cliente..."
                />
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => loadClientes(qCliente.trim())}
                  disabled={loadingClientes || !qCliente.trim()}
                >
                  {loadingClientes ? <Loader2 className="animate-spin" size={16} /> : "Buscar"}
                </Button>
              </div>

              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {clientes.length === 0 ? (
                  <div className="p-3 text-sm text-slate-500">Busque e selecione um cliente.</div>
                ) : (
                  <div className="divide-y">
                    {clientes.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setClienteId(c.id)}
                        className={`w-full text-left p-3 hover:bg-slate-50 transition ${clienteId === c.id ? "bg-slate-50" : ""
                          }`}
                      >
                        <div className="font-medium text-slate-900">{c.nome}</div>
                        <div className="text-xs text-slate-500">
                          {c.documento ? `Doc: ${c.documento} • ` : ""}
                          {c.telefone ? `Tel: ${c.telefone}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {!clienteId && <p className="text-xs text-slate-500">Obrigatório para criar a OS.</p>}
            </div>

            {/* Veículo */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Veículo (opcional)</label>

              <select
                value={veiculoId}
                onChange={(e) => setVeiculoId(e.target.value)}
                disabled={!clienteId || loadingVeiculos}
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
              >
                <option value="">
                  {!clienteId ? "Selecione um cliente primeiro" : loadingVeiculos ? "Carregando veículos..." : "Sem veículo"}
                </option>
                {veiculos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {(v.placa ?? "—")} • {(v.marca ?? "")} {(v.modelo ?? "")} {(v.ano ? `(${v.ano})` : "")}
                  </option>
                ))}
              </select>
            </div>

            {/* Observações */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Observação do cliente</label>
                <textarea
                  value={observacaoCliente}
                  onChange={(e) => setObservacaoCliente(e.target.value)}
                  className="min-h-[90px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="Ex: barulho ao frear, vibração em alta..."
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Diagnóstico inicial</label>
                <textarea
                  value={diagnostico}
                  onChange={(e) => setDiagnostico(e.target.value)}
                  className="min-h-[90px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="Ex: suspeita de pastilha, revisar suspensão..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Link to="/os">
                <Button type="button" variant="outline" disabled={saving}>
                  Cancelar
                </Button>
              </Link>

              <Button type="submit" disabled={disabled} className="gap-2">
                <Save size={16} />
                {saving ? "Criando..." : "Criar OS"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}
